import { StyleSheet, View, type ViewProps } from "react-native";

import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { AppText } from "./AppText";
import { Icon } from "./Icon";
import { Row } from "./Row";
import { Stack } from "./Stack";

type AlertTone = "info" | "success" | "warning" | "danger";

export function Alert({
  title,
  description,
  tone = "info",
  style,
  ...props
}: ViewProps & {
  title: string;
  description?: string;
  tone?: AlertTone;
}) {
  const { stylesheet, toneStyles } = useThemedStyles(makeStyles);
  return (
    <View
      accessibilityRole="alert"
      style={[stylesheet.alert, toneStyles[tone], style]}
      {...props}
    >
      <Row align="start" gap="sm">
        <Icon name={toneIcons[tone]} size="sm" tone={toneIconTones[tone]} />
        <Stack gap="xs" style={stylesheet.copy}>
          <AppText weight="semibold">{title}</AppText>
          {description ? <AppText size="xs">{description}</AppText> : null}
        </Stack>
      </Row>
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    alert: {
      borderWidth: 1,
      borderRadius: radii.m,
      padding: spacing.m,
    },
    info: { borderColor: colors.border, backgroundColor: colors.surface },
    success: {
      borderColor: colors.success,
      backgroundColor: colors.successSurface,
    },
    warning: {
      borderColor: colors.warning,
      backgroundColor: colors.warningSurface,
    },
    danger: {
      borderColor: colors.danger,
      backgroundColor: colors.dangerSurface,
    },
    copy: { flex: 1 },
  });
  const toneStyles = {
    info: stylesheet.info,
    success: stylesheet.success,
    warning: stylesheet.warning,
    danger: stylesheet.danger,
  };
  return { stylesheet, toneStyles };
};
const toneIcons = {
  info: "info",
  success: "circle-check",
  warning: "triangle-alert",
  danger: "circle-alert",
} as const;
const toneIconTones = {
  info: "default",
  success: "success",
  warning: "default",
  danger: "danger",
} as const;
